import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ReloadOutlined } from '@ant-design/icons';
import { Table, Modal, Tooltip, Row, Button } from 'antd';
import _ from 'lodash';
import qs from 'qs';
import TextTruncate from 'react-text-truncate';
import ReactHtmlParser from 'react-html-parser';
import PageTitle from '../../components/PageTitle';
import TableActionBar from '../../components/TableActionBar';
import BaseSelect from '../../components/Elements/BaseSelect';
import { checkPermission } from '../../utils/permission';
import { cleanObject, isValidDate, stripHtml } from '../../utils/helpers';
import { fetchActiveScheduleAreas } from '../../redux/actions/scheduleAreaActions';
import {
    fetchScheduleAreaFunctions,
    fetchScheduleAreaFunction,
    removeScheduleAreaFunctions
} from '../../redux/actions/scheduleAreaFunctionActions';
import ScheduleAreaFunctionForm from './ScheduleAreaFunctionForm';
import ScheduleAreaFunctionDetail from './ScheduleAreaFunctionDetail';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/schedule-area-functions', breadcrumbName: 'Chức năng khu vực' }
];

class ScheduleAreaFunctionList extends Component {
    state = {
        isLoading: false,
        selectedRowKeys: [],
        isOpenForm: false,
        isOpenDetail: false,
        currentData: null,
        currentId: null
    };

    componentDidMount() {
        this.loadData();
        this.props.fetchActiveScheduleAreas().catch(() => null);
    }

    componentDidUpdate(previousProps) {
        if (previousProps.location.search !== this.props.location.search) this.loadData();
    }

    loadData = () => {
        const query = qs.parse(this.props.location.search.slice(1));
        this.setState({ isLoading: true });
        return this.props.fetchScheduleAreaFunctions(query)
            .catch(() => null)
            .finally(() => this.setState({ isLoading: false }));
    };

    changeFilter = (name, value) => {
        const query = cleanObject({
            ...qs.parse(this.props.location.search.slice(1)),
            [name]: value,
            page: undefined
        });
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    changeTable = (pagination, sorter) => {
        const query = { ...qs.parse(this.props.location.search.slice(1)) };
        if (!_.isEmpty(pagination)) {
            query.page = pagination.current;
            query.per_page = pagination.pageSize;
        }
        if (sorter.order) {
            query.sort_field = sorter.field;
            query.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        } else {
            delete query.sort_field;
            delete query.sort_type;
        }
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    openDetail = id => {
        this.setState({ currentId: id });
        this.props.fetchScheduleAreaFunction(id)
            .then(currentData => this.setState({ isOpenDetail: true, currentData }))
            .catch(() => null);
    };

    remove = ids => this.props.removeScheduleAreaFunctions(ids).then(() => {
        this.setState({ selectedRowKeys: [] });
        this.loadData();
    }).catch(() => null);

    confirmRemove = ids => Modal.confirm({
        title: 'Cảnh báo',
        content: 'Bạn chắc chắn muốn xoá những chức năng đã chọn?',
        okText: 'OK',
        cancelText: 'Huỷ',
        onOk: () => this.remove(ids)
    });

    reloadDetail = () => this.props.fetchScheduleAreaFunction(this.state.currentId)
        .then(currentData => this.setState({ currentData }))
        .catch(() => null);

    render() {
        const { items, pagination, activeAreas } = this.props;
        const { isLoading, selectedRowKeys, isOpenForm, isOpenDetail, currentData } = this.state;
        const query = qs.parse(this.props.location.search.slice(1));
        const columns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: true },
            {
                title: 'Khu vực làm việc', dataIndex: 'area_name', key: 'area_name', sorter: true,
                render: (text, record) => checkPermission('schedule_area_functions', 'detail') ? (
                    <Tooltip title="Xem chi tiết" placement="right">
                        <div className="color-primary item-action-btn" onClick={() => this.openDetail(record.id)}>{text}</div>
                    </Tooltip>
                ) : text
            },
            {
                title: 'Mô tả chức năng', dataIndex: 'description', key: 'description', width: 280,
                render: text => <TextTruncate line={1} truncateText="…" text={stripHtml(text || '')} />
            },
            {
                title: 'Ghi chú', dataIndex: 'note', key: 'note', width: 220,
                render: text => (
                    <Tooltip placement="right" title={ReactHtmlParser(text || '')}>
                        <div style={{ wordBreak: 'break-word' }}>
                            <TextTruncate line={1} truncateText="…" text={stripHtml(text || '')} />
                        </div>
                    </Tooltip>
                )
            },
            { title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', sorter: true, width: 150, render: isValidDate },
            { title: 'Người tạo', dataIndex: 'created_user', key: 'created_user', sorter: true, width: 180 }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Chức năng khu vực" />
                <TableActionBar
                    isShowAddButton={checkPermission('schedule_area_functions', 'create')}
                    isShowDeleteButton={checkPermission('schedule_area_functions', 'remove')}
                    onAdd={() => this.setState({ isOpenForm: true, currentData: null })}
                    onDelete={() => this.confirmRemove(selectedRowKeys)}
                    disabled={!selectedRowKeys.length}
                    showFilter={false}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                    filters={[]}
                >
                    <BaseSelect
                        options={activeAreas}
                        optionValue="id"
                        optionLabel="name"
                        placeholder="Khu vực"
                        showSearch
                        style={{ width: 200 }}
                        value={query.schedule_area_id ? Number(query.schedule_area_id) : undefined}
                        onChange={value => this.changeFilter('schedule_area_id', value)}
                        className="table-button"
                    />
                    <Button className="table-button" icon={<ReloadOutlined />} loading={isLoading} onClick={this.loadData}>Làm mới</Button>
                </TableActionBar>
                {selectedRowKeys.length ? <Row className="mb-2">Đang chọn {selectedRowKeys.length} bản ghi</Row> : null}
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={items}
                    columns={columns}
                    loading={isLoading}
                    rowSelection={{ selectedRowKeys, onChange: keys => this.setState({ selectedRowKeys: keys }) }}
                    pagination={{
                        pageSize: pagination.perPage,
                        current: pagination.currentPage,
                        total: pagination.total,
                        showTotal: total => `Tổng ${total} bản ghi`
                    }}
                    onChange={(page, filters, sorter) => this.changeTable(page, sorter)}
                    scroll={{ x: 'max-content' }}
                />
                <ScheduleAreaFunctionForm
                    visible={isOpenForm}
                    currentData={currentData}
                    reloadData={this.loadData}
                    onCancel={() => this.setState({ isOpenForm: false, currentData: null })}
                />
                <ScheduleAreaFunctionDetail
                    visible={isOpenDetail}
                    currentData={currentData}
                    reloadList={this.loadData}
                    reloadData={this.reloadDetail}
                    onCancel={() => this.setState({ isOpenDetail: false, currentData: null })}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    items: state.scheduleAreaFunctionReducer.items,
    pagination: state.scheduleAreaFunctionReducer.pagination,
    activeAreas: state.scheduleAreaReducer.activeItems
});

const mapDispatchToProps = dispatch => ({
    fetchScheduleAreaFunctions: filters => dispatch(fetchScheduleAreaFunctions(filters)),
    fetchScheduleAreaFunction: id => dispatch(fetchScheduleAreaFunction(id)),
    removeScheduleAreaFunctions: ids => dispatch(removeScheduleAreaFunctions(ids)),
    fetchActiveScheduleAreas: () => dispatch(fetchActiveScheduleAreas())
});

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleAreaFunctionList);
